import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { limpiarEstadoOidc, tomarRutaPrevia } from "@/lib/api";

/**
 * Retorno del proveedor de identidad.
 *
 * Si el canje del código falla —state perdido entre pestañas, resto de un
 * intento anterior, enlace abierto en otro navegador— react-oidc-context deja
 * la sesión sin autenticar y aquí no pasaba nada más: la pantalla se quedaba en
 * «Procesando autenticación…» para siempre. Keycloak sí registraba el LOGIN, así
 * que desde fuera parecía que el usuario había entrado. Ahora el motivo se ve y
 * hay una salida: reintentar desde cero, barriendo el estado a medias.
 */
export function AuthCallback() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated) {
      // Volver a donde estaba el usuario cuando se le pidió autenticarse, no
      // siempre al inicio.
      navigate(tomarRutaPrevia() ?? "/", { replace: true });
    }
  }, [auth.isAuthenticated, navigate]);

  const reintentar = () => {
    limpiarEstadoOidc();
    void auth.signinRedirect();
  };

  if (auth.error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app px-4">
        <div className="w-full max-w-md rounded-2xl border border-edge bg-surface p-6 shadow-card">
          <span className="grid h-12 w-12 place-items-center rounded-xl bg-estado-warn-bg text-estado-warn">
            <AlertTriangle className="h-6 w-6" />
          </span>
          <h1 className="mt-4 text-lg font-semibold text-fg">
            No se pudo completar el inicio de sesión
          </h1>
          <p className="mt-2 text-sm text-fg-muted">
            Tu identidad se verificó, pero esta pestaña no pudo terminar el
            intercambio con el servidor de autenticación. Suele ocurrir cuando el
            acceso se inició en otra pestaña o en otro navegador.
          </p>
          <p className="mt-3 rounded-md bg-surface-2 p-3 font-mono text-xs text-fg-subtle">
            {auth.error.message}
          </p>
          <button
            onClick={reintentar}
            className="mt-4 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-npmx-700 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-npmx-800"
          >
            <RefreshCw className="h-4 w-4" />
            Reintentar el acceso
          </button>
          <p className="mt-3 text-center text-xs text-fg-subtle">
            Si vuelve a fallar, cierra las demás pestañas de SICEN y usa un solo
            navegador.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center text-fg-subtle">
      Procesando autenticación…
    </div>
  );
}
